<template>
  <m-select
    :open="open"
    :value="selectedOptions"
    :items="options"
    :read-only="readOnly"
    :value-key="valueKey"
    :item-text="itemText"
    :max-tag-text-length="maxTagTextLength"
    full-width
    tags
    multiple
    :placeholder="$t('optionScope.placeholder')"
    keep-open-on-click
    return-object
    @update:open="emit('update:open', $event)"
    @update:value="updateSelectedOptions"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>
    <template
      v-if="!isEmpty"
      #overlay-header
    >
      <m-content :padding-top="4">
        <m-content :padding-x="5">
          <quick-filter-header
            :label="scopeItem.text"
            @delete="emit('delete')"
            @add-to-advanced="emit('addToAdvanced')"
          >
            <template #op>
              <m-select
                :value="selectedType"
                :items="operatorTypes"
                :read-only="readOnly"
                xs
                hide-border
                light
                :trigger-style="{ textTransform: 'lowercase' }"
                @input="updateSelectedType"
              />
            </template>
          </quick-filter-header>
        </m-content>
      </m-content>
    </template>
    <template
      v-else
      #overlay
    >
      <m-card padding-xxs>
        <m-content :padding-x="2">
          <quick-filter-header
            :label="scopeItem.text"
            @delete="emit('delete')"
            @add-to-advanced="emit('addToAdvanced')"
          >
            <template #op>
              <m-select
                :value="selectedType"
                :items="operatorTypes"
                :read-only="readOnly"
                xs
                hide-border
                light
                :trigger-style="{ textTransform: 'lowercase' }"
                @input="updateSelectedType"
              />
            </template>
          </quick-filter-header>
        </m-content>
      </m-card>
    </template>
  </m-select>
</template>

<script setup>
import QuickFilterHeader from '@/components/filter/quick/QuickFilterHeader.vue';
import useScopeItem from '@/composables/filter/scope-item';
import { AND, EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { buildIconFromEntity } from 'shared/lib/icon';
import { computed } from 'vue';
import { propertyType } from 'shared/constants.json';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  scopeItem: {
    type: Object,
    required: true,
  },
  node: {
    type: Object,
    required: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  valueKey: {
    type: String,
    default: 'uid',
  },
  itemText: {
    type: String,
    default: 'label.en',
  },
  maxTagTextLength: {
    type: Number,
    default: 0,
  },
  hideIsEmpty: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:open', 'update:scope', 'update:not-operator', 'delete', 'addToAdvanced']);

const { operatorTypes: getOperatorTypes } = useScopeItem();
const operatorTypes = getOperatorTypes(propertyType.options, { hideEmpty: props.hideIsEmpty });

const options = computed(() => props.options.map((o) => ({ ...o, icon: buildIconFromEntity(o) })));

const isNot = computed(() => props.node.op === NOT);
const scope = computed(() => props.node.children[0].scope);
const isEmpty = computed(() => scope.value.isEmpty);

const selectedType = computed(() => {
  if (isNot.value) {
    return isEmpty.value ? NOT_EMPTY : NOT;
  }
  return isEmpty.value ? EMPTY : AND;
});
const updateSelectedType = (value) => {
  if (![NOT, NOT_EMPTY].includes(value)) {
    emit('update:not-operator', AND);
  } else {
    emit('update:not-operator', NOT);
  }
  if ([EMPTY, NOT_EMPTY].includes(value)) {
    emit('update:scope', { ...scope.value, isEmpty: true });
    return;
  }
  emit('update:scope', { ...scope.value, isEmpty: false });
};

const selectedOptions = computed(() => scope.value.selectedOptions);
const updateSelectedOptions = (value) => {
  if (JSON.stringify(selectedOptions.value) === JSON.stringify(value)) {
    return;
  }
  emit('update:scope', { ...scope.value, selectedOptions: value });
};

</script>

<style scoped lang="scss">
</style>
